/**
 * useUserEntitlements - Fetches and caches user entitlements from entitlement codes
 *
 * Same pattern as useSubscription:
 * - localStorage cache with 5-min TTL for instant UX
 * - Background fetch for fresh data
 * - Singleton instance via useSharedUserEntitlements()
 */

import { ref, computed, inject, type Ref, type ComputedRef } from 'vue'
import type { SupabaseClient } from '@supabase/supabase-js'
import { resolveSupabase } from './schools/client'
import { bootFetch } from '../playback/signedInBoot'
import type { UserEntitlement } from '@ssi/core'
import { entitlementsToApply } from './entitlementLessonLatch'
import { hasPlaybackBeenLive } from '../playback/playbackLiveness'
import { notePupilFromEntitlementAnswer } from './useVoiceEntitlement'
import { legacyAnswerPending, noteLegacyFromEntitlementAnswer } from './legacyAccessNote'

// ============================================================================
// STORAGE
// ============================================================================

const CACHE_KEY = 'ssi_user_entitlements'
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

interface CachedEntitlements {
  entitlements: UserEntitlement[]
  cachedAt: number
  /** The auth uid this answer was given for. An answer is only ever read back
   *  for the same person (job #895): the cache is per device, not per account. */
  ownerId?: string | null
}

/** Bounds the "unknown" wait for one ask, like useSubscription's hydration
 *  timeout: a request that never answers ends the wait holding whatever this
 *  identity already had (nothing, for a new one), never an open door. */
export const ENTITLEMENTS_ANSWER_TIMEOUT_MS = 8000

// ============================================================================
// COMPOSABLE
// ============================================================================

export interface UseUserEntitlementsReturn {
  /** Current active entitlements */
  entitlements: Ref<UserEntitlement[]>
  /** Whether user has any active entitlement */
  hasEntitlement: ComputedRef<boolean>
  /** Whether we're loading */
  isLoading: Ref<boolean>
  /** True once the server (or a definite "no token") has answered for the
   *  CURRENT identity. False = unknown, which gated UI treats as WAITING:
   *  never locked (no padlock, no upgrade page) and never open (nothing
   *  premium is granted on an unknown). */
  isKnown: Ref<boolean>
  /** The identity changed (sign-in, join, sign-out): forget any answer that
   *  was not given for `userId`, void every ask still in flight, and ask again. */
  switchIdentity: (userId: string | null) => Promise<void>
  /** Same person, but something server-side just changed (a code redeemed,
   *  a class joined): void asks in flight and ask again. */
  revalidate: () => Promise<void>
  /** Initialize — call from App.vue after supabase + auth are ready */
  initialize: () => Promise<void>
  /** Refresh from API */
  refresh: () => Promise<void>
  /** At the wall with no old-app answer yet (ask 681): ask the server to ask
   *  the old app now, and take the entitlements it answers with. Whatever
   *  happens, the old-app wait is over for this identity when it returns. */
  settleOldApp: () => Promise<void>
  /** Clear local cache */
  clearCache: () => void
}

export function useUserEntitlements(): UseUserEntitlementsReturn {
  const supabaseRef = inject<{ value: SupabaseClient | null }>('supabase')

  const entitlements = ref<UserEntitlement[]>([])
  const isLoading = ref(false)
  const isKnown = ref(false)
  // THE GENERATION (job #895, Astra's refutation of #873). Every ask captures
  // it; an identity change or a revalidation bumps it; an answer whose
  // generation is no longer current is dropped whole. So account A's late
  // reply can never land on account B, and a fetch started before a class
  // join finished can never overwrite the answer asked for after it.
  let generation = 0

  const hasEntitlement = computed(() => entitlements.value.length > 0)

  // ============================================================================
  // CACHE
  // ============================================================================

  function loadFromCache(ignoreAge = false): CachedEntitlements | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (!cached) return null
      const data: CachedEntitlements = JSON.parse(cached)
      if (!ignoreAge && Date.now() - data.cachedAt > CACHE_TTL_MS) return null
      return data
    } catch {
      return null
    }
  }

  function saveToCache(items: UserEntitlement[], ownerId: string | null): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        entitlements: items,
        cachedAt: Date.now(),
        ownerId,
      }))
    } catch {
      // Ignore storage errors
    }
  }

  function clearCache(): void {
    // Sign-out: nothing held for the last account may carry to the next one.
    generation++
    heldFor = null
    sessionBaseline = []
    entitlements.value = []
    // The pupil flags belong to whoever this answer was for (review #118).
    notePupilFromEntitlementAnswer(null)
    noteLegacyFromEntitlementAnswer(null)
    try {
      localStorage.removeItem(CACHE_KEY)
    } catch {
      // Ignore
    }
  }

  // ============================================================================
  // API
  // ============================================================================

  async function getSession(): Promise<{ token: string; userId: string | null } | null> {
    const client = resolveSupabase(supabaseRef)
    if (!client) return null
    try {
      const { data: { session } } = await client.auth.getSession()
      return session?.access_token ? { token: session.access_token, userId: session.user?.id ?? null } : null
    } catch {
      return null
    }
  }

  async function fetchEntitlements(url = '/api/entitlement/user'): Promise<void> {
    const gen = generation
    const current = () => gen === generation
    // The wait is bounded: an ask that never answers ends it holding what
    // this identity already has. Only for THIS ask — a newer one owns the flag.
    const bound = setTimeout(() => { if (current()) isKnown.value = true }, ENTITLEMENTS_ANSWER_TIMEOUT_MS)
    try {
      await fetchFor(gen, current, url)
    } finally {
      clearTimeout(bound)
    }
  }

  async function fetchFor(gen: number, current: () => boolean, url: string): Promise<void> {
    const session = await getSession()
    if (!current()) return
    if (!session) {
      entitlements.value = []
      notePupilFromEntitlementAnswer(null)
      noteLegacyFromEntitlementAnswer(null)
      isKnown.value = true
      return
    }
    const { token, userId } = session

    try {
      const response = await bootFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (!current()) return
      if (!response.ok) {
        if (response.status === 401) {
          clearCache()
          generation = gen // clearCache voids asks in flight; this one is still the answer
          entitlements.value = []
          notePupilFromEntitlementAnswer(null)
          noteLegacyFromEntitlementAnswer(null)
          isKnown.value = true
          return
        }
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()
      if (!current()) return
      // The pupil flags ride the same answer, and must be in place before
      // isKnown flips: the player's resume gate decides the wall on it (#115).
      notePupilFromEntitlementAnswer(data)
      noteLegacyFromEntitlementAnswer(data)
      const items: UserEntitlement[] = (data.entitlements || []).map((e: any) => ({
        accessType: e.access_type,
        grantedCourses: e.granted_courses,
        expiresAt: e.expires_at,
        ...(e.source ? { source: e.source } : {}),
      }))

      // Access taken away after the first sound waits for the next app start
      // (entitlementLessonLatch.ts); the cache gets the fresh answer either way.
      entitlements.value = entitlementsToApply({
        applied: entitlements.value,
        baseline: sessionBaseline,
        fresh: items,
        lessonStarted: hasPlaybackBeenLive(),
      })
      heldFor = userId
      saveToCache(items, userId)
    } catch (err) {
      console.error('[useUserEntitlements] Fetch error:', err)
    } finally {
      // Answered, or failed: either way the wait is over for THIS identity. A
      // failure keeps what this identity already held (its own cached answer,
      // or nothing) rather than hiding the UI forever. A stale ask touches nothing.
      if (current()) isKnown.value = true
    }
  }

  async function refresh(): Promise<void> {
    isLoading.value = true
    try {
      await fetchEntitlements()
    } finally {
      isLoading.value = false
    }
  }

  let settling: Promise<void> | null = null
  function settleOldApp(): Promise<void> {
    if (settling) return settling
    const gen = generation
    settling = (async () => {
      isLoading.value = true
      try {
        await fetchEntitlements('/api/entitlement/user?settleLegacy=1')
      } finally {
        isLoading.value = false
        // A failed ask must not hold the learner at the wall for ever: the
        // wall decides on what is held. A newer identity owns its own flag.
        if (gen === generation) legacyAnswerPending.value = false
        settling = null
      }
    })()
    return settling
  }

  // ============================================================================
  // INIT
  // ============================================================================

  const cached = loadFromCache()
  if (cached) {
    entitlements.value = cached.entitlements
  }
  // What this learner held when the app last spoke to the server, whatever
  // its age — the latch's floor if the boot fetch lands after the first sound.
  const lastKnown = loadFromCache(true)
  let sessionBaseline: UserEntitlement[] = lastKnown?.entitlements ?? []
  // Whose answer the values above are. A cache written before job #895 has no
  // owner: it is trusted for the boot (the device's own last session, as
  // before) and replaced by the first answer, which records one.
  let heldFor: string | null | undefined = lastKnown ? lastKnown.ownerId : null

  async function initialize(): Promise<void> {
    if (resolveSupabase(supabaseRef)) await fetchEntitlements()
  }

  async function switchIdentity(userId: string | null): Promise<void> {
    generation++
    isKnown.value = false
    if (!userId || heldFor !== userId) {
      // Nothing held belongs to this person: drop it, and its latch floor,
      // so the wait shows nothing of the last account's.
      entitlements.value = []
      sessionBaseline = []
      // And the last account's pupil flags: B must never inherit A's class
      // limit or A's parent-ask class while B's answer is out or failing (#118).
      notePupilFromEntitlementAnswer(null)
      noteLegacyFromEntitlementAnswer(null)
      heldFor = userId
      try { localStorage.removeItem(CACHE_KEY) } catch { /* ignore */ }
    }
    await refresh()
  }

  async function revalidate(): Promise<void> {
    generation++
    isKnown.value = false
    await refresh()
  }

  return {
    entitlements,
    hasEntitlement,
    isLoading,
    isKnown,
    switchIdentity,
    revalidate,
    initialize,
    refresh,
    settleOldApp,
    clearCache,
  }
}

// ============================================================================
// SHARED INSTANCE
// ============================================================================

let sharedInstance: ReturnType<typeof useUserEntitlements> | null = null

export function useSharedUserEntitlements(): UseUserEntitlementsReturn {
  if (!sharedInstance) {
    sharedInstance = useUserEntitlements()
  }
  return sharedInstance
}
